import axios from "axios";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./_RepairTariffManager.scss";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import { getApiEndpoint } from "../../../../utils/apiConfig";

const RepairTariffManager = () => {
  const [repairTariffAdmin, setRepairTariffAdmin] = useState([]);
  const [repairTariff, setRepairTariff] = useState({
    name: { az: "", en: "", ru: "" },
    description: { az: "", en: "", ru: "" },
    minValue: "",
    maxValue: "",
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    fetchRepairTariffs();
  }, []);

  const fetchRepairTariffs = () => {
    const api = axiosWithAuth();
    api
      .get("/repair-tariffs/admin")
      .then((response) => {
        setRepairTariffAdmin(response.data);
      })
      .catch((error) => {
        console.error("Failed to fetch repair tariffs:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to fetch repair tariffs. Please try again.",
          icon: "error",
          timer: 2000,
          showConfirmButton: false
        });
      });
  };

  const handleInput = (event, field, lang) => {
    const { value, type, checked } = event.target;
    
    if (type === "checkbox") {
      setRepairTariff((prev) => ({
        ...prev,
        status: checked,
      }));
    } else if (field === "name" || field === "description") {
      setRepairTariff((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [lang]: value,
        },
      }));
    } else {
      setRepairTariff((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const api = axiosWithAuth();
      await api.put(
        `/repair-tariffs/${id}`,
        { ...repairTariff, status: !currentStatus }
      );
      fetchRepairTariffs();
      Swal.fire({
        title: "Success!",
        text: "Status updated successfully",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to update status",
        icon: "error",
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const api = axiosWithAuth();
      if (isEditing) {
        await api.put(
          `/repair-tariffs/${editId}`,
          repairTariff
        );
        Swal.fire({
          title: "Success!",
          text: "Repair tariff updated successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        await api.post(
          "/repair-tariffs",
          repairTariff
        );
        Swal.fire({
          title: "Success!",
          text: "Repair tariff added successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      }
      setIsEditing(false);
      setEditId(null);
      setRepairTariff({
        name: { az: "", en: "", ru: "" },
        description: { az: "", en: "", ru: "" },
        minValue: "",
        maxValue: "",
        status: true,
      });
      fetchRepairTariffs();
    } catch (error) {
      console.error("Failed to save repair tariff:", error);
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to save repair tariff",
        icon: "error",
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  const handleEdit = (tariff) => {
    setIsEditing(true);
    setEditId(tariff.id);
    setRepairTariff({
      name: tariff.name,
      description: tariff.description,
      minValue: tariff.minValue,
      maxValue: tariff.maxValue,
      status: tariff.status,
    });
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "This action cannot be undone!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        showDenyButton: true,
        denyButtonText: 'Force Delete',
        denyButtonColor: '#dc3545'
      });

      if (result.isConfirmed || result.isDenied) {
        const forceDelete = result.isDenied;
        const url = `/repair-tariffs/${id}${forceDelete ? '?force=true' : ''}`;
        
        const api = axiosWithAuth();
        await api.delete(url);
        
        await fetchRepairTariffs();
        
        Swal.fire({
          title: 'Success!',
          text: forceDelete ? 'Repair tariff has been force deleted.' : 'Repair tariff has been deleted.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Failed to delete repair tariff:', error);
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to delete repair tariff.',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  return (
    <div className="repair-tariff-manager">
      <h2>Repair Tariff Manager</h2>
      
      <div className="form-section">
        <h3>{isEditing ? 'Edit Repair Tariff' : 'Add New Repair Tariff'}</h3>
        
        <div className="form-group">
          <label>Name (AZ)</label>
          <input
            type="text"
            value={repairTariff.name.az}
            onChange={(e) => handleInput(e, "name", "az")}
            placeholder="Enter name in Azerbaijani"
            required
          />
        </div>
        <div className="form-group">
          <label>Name (EN)</label>
          <input
            type="text"
            value={repairTariff.name.en}
            onChange={(e) => handleInput(e, "name", "en")}
            placeholder="Enter name in English"
            required
          />
        </div>
        <div className="form-group">
          <label>Name (RU)</label>
          <input
            type="text"
            value={repairTariff.name.ru}
            onChange={(e) => handleInput(e, "name", "ru")}
            placeholder="Enter name in Russian"
            required
          />
        </div>

        <div className="form-group">
          <label>Description (AZ)</label>
          <input
            type="text"
            value={repairTariff.description.az}
            onChange={(e) => handleInput(e, "description", "az")}
            placeholder="Enter description in Azerbaijani"
            required
          />
        </div>
        <div className="form-group">
          <label>Description (EN)</label>
          <input
            type="text"
            value={repairTariff.description.en}
            onChange={(e) => handleInput(e, "description", "en")}
            placeholder="Enter description in English"
            required
          />
        </div>
        <div className="form-group">
          <label>Description (RU)</label>
          <input
            type="text"
            value={repairTariff.description.ru}
            onChange={(e) => handleInput(e, "description", "ru")}
            placeholder="Enter description in Russian"
            required
          />
        </div>

        <div className="form-group">
          <label>Minimum Value</label>
          <input
            type="number"
            value={repairTariff.minValue}
            onChange={(e) => handleInput(e, "minValue")}
            placeholder="Enter minimum value"
            required
          />
        </div>
        <div className="form-group">
          <label>Maximum Value</label>
          <input
            type="number"
            value={repairTariff.maxValue}
            onChange={(e) => handleInput(e, "maxValue")}
            placeholder="Enter maximum value"
            required
          />
        </div>
        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            checked={repairTariff.status}
            onChange={(e) => handleInput(e, "status")}
            id="isActive"
          />
          <label htmlFor="isActive">Active</label>
        </div>
        <div className="button-group">
          <button className="submit-btn" onClick={handleSubmit}>
            {isEditing ? 'Update' : 'Add'} Repair Tariff
          </button>
          {isEditing && (
            <button className="cancel-btn" onClick={() => {
              setIsEditing(false);
              setEditId(null);
              setRepairTariff({
                name: { az: "", en: "", ru: "" },
                description: { az: "", en: "", ru: "" },
                minValue: "",
                maxValue: "",
                status: true,
              });
            }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="table-section">
        <table>
          <thead>
            <tr>
              <th>Name (AZ)</th>
              <th>Name (EN)</th>
              <th>Name (RU)</th>
              <th>Description (AZ)</th>
              <th>Description (EN)</th>
              <th>Description (RU)</th>
              <th>Min Value</th>
              <th>Max Value</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {repairTariffAdmin.filter(tariff => tariff.status).map((tariff) => (
              <tr key={tariff.id}>
                <td>{tariff.name.az}</td>
                <td>{tariff.name.en}</td>
                <td>{tariff.name.ru}</td>
                <td>{tariff.description.az}</td>
                <td>{tariff.description.en}</td>
                <td>{tariff.description.ru}</td>
                <td>{tariff.minValue}</td>
                <td>{tariff.maxValue}</td>
                <td>
                  <span
                    className={`status-badge ${tariff.status ? "active" : "inactive"}`}
                    onClick={() => handleStatusToggle(tariff.id, tariff.status)}
                  >
                    {tariff.status ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(tariff)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(tariff.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RepairTariffManager;
